import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Button, Form as BForm, InputGroup } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { FiLink } from "react-icons/fi";
import { fetchFeedUrl } from "../../redux/features/inventory/inventoryAction";
import { setOpenInventoryModal } from "../../redux/features/inventory/inventorySlice";
import { errorMessage } from "../../utlis/common";

interface UrlFeedModalProps {
  show: boolean;
  handleClose: () => void;
}

// ---- Helpers ----

const detectFormat = (
  contentType: string,
  url: string
): "json" | "csv" | "xml" | null => {
  const ct = contentType.toLowerCase();
  if (ct.includes("json")) return "json";
  if (ct.includes("csv")) return "csv";
  if (ct.includes("xml")) return "xml";
  // Fallback: check URL extension (before query string)
  const ext = url.toLowerCase().split("?")[0].split(".").pop();
  if (ext === "json") return "json";
  if (ext === "csv") return "csv";
  if (ext === "xml") return "xml";
  return null;
};

const parseFeedText = (
  text: string,
  format: "json" | "csv" | "xml" | null
): { data: any[]; headers: string[] } => {
  // ---- JSON ----
  if (format === "json" || format === null) {
    try {
      const parsed = JSON.parse(text);
      let arr: any[] = [];
      if (Array.isArray(parsed)) {
        arr = parsed;
      } else if (typeof parsed === "object" && parsed !== null) {
        // Find first array property (e.g. { items: [...] }, { data: [...] })
        const arrayKey = Object.keys(parsed).find((k) =>
          Array.isArray(parsed[k])
        );
        if (arrayKey) arr = parsed[arrayKey];
      }
      if (arr.length > 0 && typeof arr[0] === "object") {
        const headers = Object.keys(arr[0]);
        return { data: arr, headers };
      }
    } catch {
      // not JSON, continue
    }
  }

  // ---- CSV ----
  if (format === "csv" || format === null) {
    const rows = text
      .split(/\r?\n/)
      .map((r) => r.trim())
      .filter((r) => r.length > 0);
    if (rows.length > 1) {
      const delimiter = rows[0].includes(";") ? ";" : ",";
      const headers = rows[0]
        .split(delimiter)
        .map((h) => h.replace(/(^"|"$)/g, "").trim());
      if (headers.length > 0) {
        const data: any[] = [];
        for (let i = 1; i < rows.length; i++) {
          const values = rows[i]
            .split(delimiter)
            .map((v) => v.replace(/(^"|"$)/g, "").trim());
          const record = headers.reduce((obj: any, header, idx) => {
            const raw = values[idx] ?? "";
            obj[header] = raw !== "" && !isNaN(Number(raw)) ? Number(raw) : raw;
            return obj;
          }, {});
          data.push(record);
        }
        return { data, headers };
      }
    }
  }

  // ---- XML ----
  if (format === "xml" || format === null) {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, "application/xml");
      const parseError = xmlDoc.querySelector("parsererror");
      if (!parseError) {
        const items = xmlDoc.getElementsByTagName("item");
        if (items.length > 0) {
          const headers: string[] = [];
          Array.from(items).forEach((item) => {
            Array.from(item.children).forEach((el) => {
              if (!headers.includes(el.tagName)) headers.push(el.tagName);
            });
          });
          const data: any[] = [];
          for (let i = 0; i < items.length; i++) {
            const record: any = {};
            headers.forEach((h) => (record[h] = null));
            Array.from(items[i].children).forEach((el) => {
              const v = el.textContent?.trim() || null;
              record[el.tagName] =
                v !== null && !isNaN(Number(v)) ? Number(v) : v;
            });
            data.push(record);
          }
          return { data, headers };
        }
      }
    } catch {
      // not valid XML
    }
  }

  return { data: [], headers: [] };
};

// ---- Component ----

const UrlFeedModal: React.FC<UrlFeedModalProps> = ({ show, handleClose }) => {
  const dispatch: any = useDispatch();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClose_ = () => {
    if (!loading) {
      setUrl("");
      handleClose();
    }
  };

  const handleFetch = async () => {
    const trimmedUrl = url.trim();

    if (!trimmedUrl) {
      errorMessage("Inserisci un URL valido");
      return;
    }

    try {
      new URL(trimmedUrl);
    } catch {
      errorMessage("Il formato dell'URL non è valido");
      return;
    }

    setLoading(true);

    try {
      const result = await dispatch(fetchFeedUrl({ url: trimmedUrl }));

      if (result?.error || !result?.payload?.data) {
        setLoading(false);
        return;
      }

      const { text, contentType } = result.payload.data as {
        text: string;
        contentType: string;
      };

      if (!text) {
        errorMessage("Il feed non ha restituito dati");
        setLoading(false);
        return;
      }

      const format = detectFormat(contentType || "", trimmedUrl);
      const { data, headers } = parseFeedText(text, format);

      if (data.length === 0) {
        errorMessage(
          "Nessun dato rilevato nel feed. Formati supportati: JSON (array), CSV, XML (con tag <item>)."
        );
        setLoading(false);
        return;
      }

      // Validation: prezzo vendita <= 10000, costo articoli <= 10000, IVA <= 30
      const invalidRecords = data.filter((record) => {
        const prezzo = parseFloat(
          record["prezzo vendita"] || record["Prezzo Vendita"] || 0
        );
        const costo = parseFloat(
          record["costo articoli"] || record["Costo Articoli"] || 0
        );
        const iva = parseFloat(record["iva"] || record["IVA"] || 0);
        return prezzo > 10000 || costo > 10000 || iva > 30;
      });

      if (invalidRecords.length > 0) {
        errorMessage(
          "Alcuni record superano i limiti consentiti: Prezzo/Costo > 10.000 o IVA > 30. Correggere il feed e riprovare."
        );
        setLoading(false);
        return;
      }

      setUrl("");
      handleClose();
      dispatch(setOpenInventoryModal({ data, headers }));
    } catch (error) {
      console.error("Feed fetch error:", error);
    }

    setLoading(false);
  };

  return (
    <Modal show={show} onHide={handleClose_} centered>
      <Modal.Header closeButton className="modalHeader text-white">
        <Modal.Title>Importa da Feed URL</Modal.Title>
      </Modal.Header>

      <Modal.Body className="modal-body">
        <p className="text-muted mb-4" style={{ fontSize: "0.92rem" }}>
          Inserisci l'URL del feed di dati esterno. Il sistema recupererà i dati
          dal server e aprirà la finestra di mappatura campi.
          <br />
          <strong>Formati supportati:</strong> JSON (array o oggetto con array),
          CSV, XML (con tag <code>&lt;item&gt;</code>).
        </p>

        <BForm.Group>
          <BForm.Label className="fw-semibold">URL del Feed</BForm.Label>
          <InputGroup>
            <InputGroup.Text>
              <FiLink size={16} />
            </InputGroup.Text>
            <BForm.Control
              type="url"
              placeholder="https://esempio.com/catalogo.json"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !loading && handleFetch()}
              disabled={loading}
              autoFocus
            />
          </InputGroup>
          <BForm.Text className="text-muted" style={{ fontSize: "0.8rem" }}>
            * Formati accettati: JSON, CSV (virgola o punto e virgola), XML
          </BForm.Text>
        </BForm.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose_}
          disabled={loading}
          className="custom-close-btn"
        >
          Annulla
        </Button>
        <Button
          onClick={handleFetch}
          disabled={loading || !url.trim()}
          style={{
            backgroundColor: "#003169",
            borderColor: "#003169",
            minWidth: "140px",
          }}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Caricamento...
            </>
          ) : (
            "Carica dal Feed"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UrlFeedModal;
